import { api } from '@/api/http';
import { ROUTES } from '@/app/routes';
import { $setFeatures } from '@/entities/features-entity';
import { $setProfile } from '@/entities/profile-entity';
import { startSessionKeeper } from '@/lib/session-keeper';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { resolvePinAuthError } from './pin-auth-error';

function hapticImpact(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') {
	window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
}

function hapticError() {
	window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
}

type UsePinAuthOptions = {
	redirectTo?: string;
};

export function usePinAuth(nickname: string | null, options: UsePinAuthOptions = {}) {
	const { redirectTo = ROUTES.profile } = options;
	const [pin, setPin] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [senderDisabled, setSenderDisabled] = useState(false);
	const [success, setSuccess] = useState(false);
	const [shakeKey, setShakeKey] = useState(0);
	const pinRef = useRef('');
	const navigate = useNavigate();
	const [setProfile, setFeatures] = useUnit([$setProfile, $setFeatures]);

	const submitPin = useCallback(
		async (pinValue: string) => {
			if (!nickname) return;

			setIsSubmitting(true);
			setError(null);

			try {
				const res = await api.post('/auth', { pin: pinValue, nickname }, { suppressToast: true });
				setProfile(res.data.profile);
				if (res.data.features) {
					setFeatures(res.data.features);
				}
				if (typeof res.data.sessionExpiresIn === 'number') {
					startSessionKeeper(res.data.sessionExpiresIn, res.data.sessionRefreshBeforeSec);
				}
				hapticImpact('medium');
				setSuccess(true);
				window.setTimeout(() => navigate(redirectTo), 400);
			} catch (submitError) {
				const resolved = resolvePinAuthError(submitError);
				if (resolved.kind === 'sender-disabled') {
					setSenderDisabled(true);
					setIsSubmitting(false);
					return;
				}
				if (resolved.kind === 'wrong-pin') {
					hapticError();
					setError('Неверный PIN');
					setShakeKey(key => key + 1);
				} else {
					hapticError();
					setError(resolved.message);
				}
				pinRef.current = '';
				setPin('');
				setIsSubmitting(false);
			}
		},
		[nickname, navigate, redirectTo, setProfile, setFeatures],
	);

	const appendDigit = useCallback(
		(digit: string) => {
			if (isSubmitting) return;
			const prev = pinRef.current;
			if (prev.length >= 4) return;

			setError(null);
			hapticImpact('light');
			const next = prev + digit;
			pinRef.current = next;
			setPin(next);

			if (next.length === 4) {
				void submitPin(next);
			}
		},
		[isSubmitting, submitPin],
	);

	const deleteDigit = useCallback(() => {
		if (isSubmitting) return;
		setError(null);
		hapticImpact('light');
		const next = pinRef.current.slice(0, -1);
		pinRef.current = next;
		setPin(next);
	}, [isSubmitting]);

	useEffect(() => {
		const keys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

		function handleKeyDown(e: KeyboardEvent) {
			if (isSubmitting) return;
			if (keys.includes(e.key)) {
				appendDigit(e.key);
			} else if (e.key === 'Backspace') {
				deleteDigit();
			}
		}

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isSubmitting, appendDigit, deleteDigit]);

	return {
		pin,
		isSubmitting,
		error,
		senderDisabled,
		success,
		shakeKey,
		appendDigit,
		deleteDigit,
	};
}
